/* ai-cart-state.js (v5) — class-only toggle, add/remove, robust detection
   Requirements on the add button:
   - class ".ai-add"
   - inside <form data-type="add-to-cart-form">
   - type="submit" name="add"
   - data-variant-id="{{ current_variant.id }}"  <-- IMPORTANT
   - contains <span class="ai-plus">+</span> (CSS hides it when added)
*/
(function () {
  const BTN_SELECTOR = '.ai-add';
  const FALLBACK_EVENTS = ['cart:updated','cart:change','line-item:added','cart-drawer:updated'];

  function findButtons(root = document) {
    return Array.from(root.querySelectorAll(BTN_SELECTOR))
      .filter(btn => btn.closest('form[data-type="add-to-cart-form"]'));
  }

  function getVariantId(btn) {
    // Prefer explicit data-variant-id
    const dv = btn.getAttribute('data-variant-id');
    if (dv && /^\d+$/.test(dv)) return parseInt(dv, 10);

    // Fallback: hidden input[name="id"]
    const form = btn.closest('form[data-type="add-to-cart-form"]');
    const input = form && form.querySelector('input[name="id"]');
    const val = input && input.value;
    return val && /^\d+$/.test(val) ? parseInt(val, 10) : null;
  }

  function renderState(btn, added) {
    btn.classList.toggle('is-added', !!added);
    btn.setAttribute('aria-pressed', added ? 'true' : 'false');
  }

  function setStatesFromCartData(buttons, cart) {
    const inCart = new Set((cart.items || []).map(it => it.id));
    buttons.forEach(btn => {
      const id = getVariantId(btn);
      if (id) renderState(btn, inCart.has(id));
    });
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { headers: { Accept:'application/json' } });
    return r.json();
  }

  function debounce(fn, wait){ let t; return (...a)=>{ clearTimeout(t); t=setTimeout(()=>fn.apply(null,a),wait); }; }

  async function removeVariant(variantId) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type':'application/json', 'Accept':'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 0 })
    }).catch(()=>{});
  }

  // After an add, some themes delay pubsub; light polling catches all cases.
  async function gentleRefresh(buttons) {
    try {
      const cart = await fetchCart();
      setStatesFromCartData(buttons, cart);
    } catch {}
  }

  function init(root = document) {
    const buttons = findButtons(root);
    if (!buttons.length) return;

    const sync = debounce(async () => {
      try { setStatesFromCartData(buttons, await fetchCart()); } catch {}
    }, 120);

    // Initial
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => sync(), { once:true });
    } else {
      sync();
    }

    // Toggle remove on second click
    buttons.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        if (!btn.classList.contains('is-added')) return; // first click => let Dawn add
        const id = getVariantId(btn);
        if (!id) return;
        e.preventDefault();
        e.stopPropagation();
        btn.disabled = true;
        await removeVariant(id);
        // force state update quickly
        setTimeout(() => sync(), 150);
        setTimeout(() => sync(), 450);
        btn.disabled = false;
      }, { capture:true });
    });

    // Variant change inside form (swatch/selector)
    root.addEventListener('change', (e) => {
      const input = e.target && e.target.closest && e.target.closest('form[data-type="add-to-cart-form"] input[name="id"]');
      if (input) sync();
    });

    // Dawn pub/sub preferred (instant)
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.cartData) {
          setStatesFromCartData(buttons, event.cartData);
        } else {
          sync();
        }
      });
    }

    // Fallback events
    FALLBACK_EVENTS.forEach(evt => document.addEventListener(evt, () => sync()));

    // After submit (add), do a gentle double refresh
    root.addEventListener('submit', (e) => {
      const form = e.target && e.target.closest && e.target.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;
      setTimeout(() => gentleRefresh(buttons), 200);
      setTimeout(() => gentleRefresh(buttons), 800);
    });

    // Re-init on section loads (Customizer / dynamic)
    document.addEventListener('shopify:section:load', (e) => init(e.target));
    document.addEventListener('shopify:section:select', (e) => init(e.target));
  }

  window.AurumCartState = { init, refresh: () => init(document) };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
})();
